export interface PixQrCodeParams {
  pixKey: string;
  merchantName: string;
  merchantCity: string;
  amount?: number | null;
  txid?: string | null;
  description?: string | null;
  cep?: string | null;
  countryCode?: string | null;
  currency?: string | number | null;
}

export function generatePixPayload(params: PixQrCodeParams): string {
  const pixKey = sanitizePixKey(params.pixKey);
  const merchantName = sanitizeText(params.merchantName, 25);
  const merchantCity = sanitizeText(params.merchantCity, 15);
  const txid = sanitizeTxid(params.txid);
  const description = sanitizeDescription(params.description);
  const cep = sanitizeCep(params.cep);
  const countryCode = (params.countryCode ?? 'BR').toUpperCase();
  const currency = String(params.currency ?? '986');
  const amount = normalizeAmount(params.amount);

  validateRequired(pixKey, 'pixKey é obrigatório');
  validateRequired(merchantName, 'merchantName é obrigatório');
  validateRequired(merchantCity, 'merchantCity é obrigatório');

  const merchantAccountInfo = buildMerchantAccountInfo(pixKey, description);

  const payloadParts: string[] = [
    tlv('00', '01'),
    tlv('01', '11'),
    tlv('26', merchantAccountInfo),
    tlv('52', '0000'),
    tlv('53', currency),
  ];

  if (amount) {
    payloadParts.push(tlv('54', amount));
  }

  payloadParts.push(tlv('58', countryCode));
  payloadParts.push(tlv('59', merchantName));
  payloadParts.push(tlv('60', merchantCity));

  if (cep) {
    payloadParts.push(tlv('61', cep));
  }

  payloadParts.push(tlv('62', tlv('05', txid)));
  payloadParts.push('6304');

  const partialPayload = payloadParts.join('');
  const crc = crc16Ccitt(partialPayload);

  return `${partialPayload}${crc}`;
}

function buildMerchantAccountInfo(
  pixKey: string,
  description?: string
): string {
  const parts = [tlv('00', 'BR.GOV.BCB.PIX'), tlv('01', pixKey)];

  if (description) {
    parts.push(tlv('02', description));
  }

  return parts.join('');
}

function tlv(id: string, value: string): string {
  const length = value.length.toString().padStart(2, '0');
  return `${id}${length}${value}`;
}

function sanitizePixKey(value: string): string {
  return String(value ?? '')
    .trim()
    .replace(/\s+/g, '');
}

function sanitizeText(value: string, maxLength: number): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9\s]/g, '')
    .trim()
    .toUpperCase()
    .slice(0, maxLength);
}

function sanitizeDescription(value?: string | null): string | undefined {
  const sanitized = String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .slice(0, 72);

  return sanitized || undefined;
}

function sanitizeCep(value?: string | null): string | undefined {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits.length === 8 ? digits : undefined;
}

function sanitizeTxid(value?: string | null): string {
  const txid = String(value ?? '').trim();
  return (txid || '***').slice(0, 25);
}

function normalizeAmount(value?: number | null): string | undefined {
  if (value == null) {
    return undefined;
  }

  if (value <= 0) {
    return undefined;
  }

  return value.toFixed(2);
}

function validateRequired(value: string, message: string): void {
  if (!value) {
    throw new Error(message);
  }
}

function crc16Ccitt(str: string): string {
  let crc = 0xffff;

  for (let i = 0; i < str.length; i++) {
    crc ^= str.charCodeAt(i) << 8;

    for (let j = 0; j < 8; j++) {
      if ((crc & 0x8000) !== 0) {
        crc = (crc << 1) ^ 0x1021;
      } else {
        crc <<= 1;
      }

      crc &= 0xffff;
    }
  }

  return crc.toString(16).toUpperCase().padStart(4, '0');
}
